import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import type { ApplicationTestingTicketRow } from '@/core/models/applicationTesting.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { applicationTestingService } from '@/core/services/applicationTesting';
import { Icon } from '@/kit/Icon';
import { Menu, useMenu } from '@/kit/Menu';
import { subscribeToTags, tagsVersion, ticketTags, tagTone } from './applicationTestingTags';
import {
  dateSortValue,
  priorityColor,
  priorityGroupOf,
  priorityOptions,
  PriorityPill,
  statusColor,
  statusOptions,
  StatusPill,
  TagChips,
} from './ApplicationTestingTickets';
import { ApplicationTestingSidebar } from './ApplicationTestingSidebar';
import './ApplicationTestingTickets.scss';
import './ApplicationTestingDashboard.scss';

const DAY = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const startOfDay = (time: number) => {
  const d = new Date(time);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};
const formatDay = (time: number) => {
  const d = new Date(time);
  return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};
const shortDay = (time: number) => {
  const d = new Date(time);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

/**
 * The dashboard's status groups are the statuses a ticket can be in (statusOptions), in the same colours as the list
 * (validated for colour-blind separation; every chart also labels its values). A ticket still in a retired status
 * counts as the first group, Open.
 */
const GROUPS = statusOptions.map((s) => ({ key: s.id, label: s.name, color: statusColor(s.id) }));
type GroupKey = number;

function groupOf(statusId: number): GroupKey {
  return statusOptions.some((s) => s.id === statusId) ? statusId : statusOptions[0].id;
}

/** Each status group's headline card: its icon, and its label when that differs from the status name. */
const KPI_CARDS: Record<string, { icon: string; label?: string }> = {
  Open: { icon: 'radio_button_checked' },
  'Requirement Pending': { icon: 'assignment_late' },
  Blocked: { icon: 'block' },
  'Clarification Required': { icon: 'help_outline' },
  Invalid: { icon: 'gpp_bad' },
  'In Progress': { icon: 'settings' },
  'Ready for BA Review': { icon: 'fact_check' },
  Closed: { icon: 'check' },
  Reopened: { icon: 'replay' },
};

/** The priorities a ticket can have (priorityOptions), in their list colours, then Not Set for any priority we do
 *  not know. A retired priority counts under the priority it became (priorityGroupOf). */
const NOT_SET_COLOR = '#8B95A5';
const PRIORITY_BARS: { label: string; id: number | null; color: string }[] = [
  ...priorityOptions.map((p) => ({ label: p.name, id: p.id, color: priorityColor(p.id) })),
  { label: 'Not Set', id: null, color: NOT_SET_COLOR },
];

type RangeKey = 'all' | '7' | '14' | '30' | '90';
const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'all', label: 'All time' },
  { key: '7', label: 'Last 7 days' },
  { key: '14', label: 'Last 14 days' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
];

interface Ticket extends ApplicationTestingTicketRow {
  created: number;
  group: GroupKey;
  tags: string[];
}

/** A round axis: `count` equal steps of 1, 2 or 5 (times a power of ten) that reach at least `max`. */
function niceTicks(max: number, count: number): number[] {
  const raw = Math.max(max, 1) / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * power).find((candidate) => candidate >= raw) ?? raw;
  return Array.from({ length: count + 1 }, (_, i) => i * step);
}

function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return Math.round(((current - previous) / previous) * 100);
}

/** A headline count with its change against the previous period of the same length. */
function KpiCard({
  icon,
  color,
  value,
  label,
  change,
}: {
  icon: string;
  /** The card's accent; the total card keeps the page's own accent when omitted. */
  color?: string;
  value: number;
  label: string;
  change: number | null;
}) {
  const up = (change ?? 0) >= 0;
  return (
    <div className="atd-kpi" style={color ? ({ '--kpi': color } as CSSProperties) : undefined}>
      <span className="atd-kpi-icon">
        <Icon name={icon} />
      </span>
      <div className="atd-kpi-main">
        <span className="atd-kpi-value">{value}</span>
        <span className="atd-kpi-label">{label}</span>
      </div>
      <div className="atd-kpi-change">
        <span className={up ? 'up' : 'down'}>
          <Icon name={up ? 'arrow_upward' : 'arrow_downward'} />
          {change === null ? 'New' : `${change > 0 ? '+' : ''}${change}%`}
        </span>
        <span className="atd-kpi-period">vs last period</span>
      </div>
    </div>
  );
}

function Donut({ counts, total }: { counts: Record<GroupKey, number>; total: number }) {
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="atd-donut">
      <svg viewBox="0 0 200 200" role="img" aria-label="Tickets by status">
        <circle cx="100" cy="100" r={radius} fill="none" stroke="#EEF0F3" strokeWidth="38" />
        {total > 0 &&
          GROUPS.map((g) => {
            const length = (counts[g.key] / total) * circumference;
            const gap = counts[g.key] > 0 && length < circumference ? 2 : 0;
            const dash = (
              <circle
                key={g.key}
                cx="100"
                cy="100"
                r={radius}
                fill="none"
                stroke={g.color}
                strokeWidth="38"
                strokeDasharray={`${Math.max(length - gap, 0)} ${circumference}`}
                strokeDashoffset={-offset}
                transform="rotate(-90 100 100)"
              >
                <title>{`${g.label}: ${counts[g.key]}`}</title>
              </circle>
            );
            offset += length;
            return dash;
          })}
        <text x="100" y="98" textAnchor="middle" className="atd-donut-total">
          {total}
        </text>
        <text x="100" y="122" textAnchor="middle" className="atd-donut-caption">
          Total
        </text>
      </svg>
      <ul className="atd-legend-list">
        {GROUPS.map((g) => (
          <li key={g.key}>
            <span className="atd-swatch" style={{ background: g.color }} />
            <span className="atd-legend-name">{g.label}</span>
            <span className="atd-legend-value">
              {counts[g.key]} ({total ? ((counts[g.key] / total) * 100).toFixed(1) : '0.0'}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PriorityChart({ values }: { values: number[] }) {
  const ticks = niceTicks(Math.max(...values), 3);
  const top = ticks[ticks.length - 1];
  const chartHeight = 160;
  return (
    <div className="atd-columns">
      <div className="atd-columns-axis">
        {[...ticks].reverse().map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>
      <div className="atd-columns-plot" style={{ height: chartHeight }}>
        {ticks.map((t) => (
          <span key={t} className="atd-gridline" style={{ bottom: `${(t / top) * 100}%` }} />
        ))}
        {PRIORITY_BARS.map((bar, i) => (
          <div key={bar.label} className="atd-column">
            <span className="atd-column-value">{values[i]}</span>
            <span
              className="atd-column-bar"
              title={`${bar.label}: ${values[i]}`}
              style={{ height: `${(values[i] / top) * 100}%`, background: bar.color }}
            />
            <span className="atd-column-label">{bar.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HorizontalBars({ rows }: { rows: { label: string; value: number; color: string }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="atd-hbars">
      {rows.map((row) => (
        <li key={row.label} title={`${row.label}: ${row.value}`}>
          <span className="atd-hbar-label">{row.label}</span>
          <span className="atd-hbar-track">
            <span className="atd-hbar" style={{ width: `${(row.value / max) * 100}%`, background: row.color }} />
          </span>
          <span className="atd-hbar-value">{row.value}</span>
        </li>
      ))}
    </ul>
  );
}

function TrendChart({ days, series }: { days: number[]; series: Record<GroupKey, number[]> }) {
  const width = 460;
  const height = 150;
  const pad = { left: 30, right: 10, top: 10, bottom: 24 };
  const ticks = niceTicks(Math.max(...GROUPS.flatMap((g) => series[g.key])), 4);
  const top = ticks[ticks.length - 1];
  const x = (i: number) => pad.left + (i / Math.max(days.length - 1, 1)) * (width - pad.left - pad.right);
  const y = (v: number) => pad.top + (1 - v / top) * (height - pad.top - pad.bottom);
  return (
    <div className="atd-trend">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Status trend">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} className="atd-trend-grid" />
            <text x={pad.left - 6} y={y(t) + 3} textAnchor="end" className="atd-trend-tick">
              {t}
            </text>
          </g>
        ))}
        {days.map((d, i) =>
          i % 2 === 0 ? (
            <text key={d} x={x(i)} y={height - 6} textAnchor="middle" className="atd-trend-tick">
              {shortDay(d)}
            </text>
          ) : null,
        )}
        {GROUPS.map((g) => (
          <g key={g.key}>
            <polyline
              fill="none"
              stroke={g.color}
              strokeWidth="2"
              points={series[g.key].map((v, i) => `${x(i)},${y(v)}`).join(' ')}
            />
            {series[g.key].map((v, i) => (
              <circle key={i} cx={x(i)} cy={y(v)} r="3.5" fill={g.color} stroke="#fff" strokeWidth="1.5">
                <title>{`${g.label}, ${formatDay(days[i])}: ${v}`}</title>
              </circle>
            ))}
          </g>
        ))}
      </svg>
      <div className="atd-legend-row">
        {GROUPS.map((g) => (
          <span key={g.key}>
            <span className="atd-swatch" style={{ background: g.color }} />
            {g.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Application Testing at a glance: headline counts, status/priority/tag breakdowns, recent tickets and the trend. */
export function ApplicationTestingDashboard() {
  const navigate = useNavigate();
  const rangeMenu = useMenu();
  const [rows, setRows] = useState<ApplicationTestingTicketRow[]>([]);
  const [range, setRange] = useState<RangeKey>('all');
  const [isSaving, setIsSaving] = useState(false);
  // Read once when the page opens, so the ranges don't shift while it is being looked at.
  const [todayStart] = useState(() => startOfDay(Date.now()));

  const load = useCallback(() => {
    applicationTestingService.list({ searchText: null, status: null, priority: null }).then(
      (response) => setRows(response.ticketList),
      (error) => notifyError(error),
    );
  }, []);

  useEffect(load, [load]);

  const tagsSeen = useSyncExternalStore(subscribeToTags, tagsVersion);

  const tickets: Ticket[] = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        created: startOfDay(dateSortValue(row.date)),
        group: groupOf(row.statusId),
        tags: ticketTags(row),
      })),
    // tagsSeen: tags chosen by hand on the list or a ticket change the tag breakdown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, tagsSeen],
  );

  const today = todayStart;
  const earliest = tickets.length ? Math.min(...tickets.map((t) => t.created)) : today;
  const latest = tickets.length ? Math.max(today, ...tickets.map((t) => t.created)) : today;
  const rangeStart = range === 'all' ? earliest : today - (Number(range) - 1) * DAY;
  const rangeEnd = range === 'all' ? latest : today;
  const span = rangeEnd - rangeStart + DAY;

  const inRange = tickets.filter((t) => t.created >= rangeStart && t.created <= rangeEnd);
  const previous = tickets.filter((t) => t.created >= rangeStart - span && t.created < rangeStart);

  const countBy = (list: Ticket[]) =>
    GROUPS.reduce(
      (acc, g) => ({ ...acc, [g.key]: list.filter((t) => t.group === g.key).length }),
      {} as Record<GroupKey, number>,
    );
  const counts = countBy(inRange);
  const previousCounts = countBy(previous);

  const priorityValues = PRIORITY_BARS.map(
    (bar) => inRange.filter((t) => priorityGroupOf(t.priorityId) === bar.id).length,
  );

  const tagCounts = new Map<string, number>();
  inRange.forEach((t) => t.tags.forEach((tag) => tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1)));
  const tagRows = [...tagCounts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const topTags = tagRows.slice(0, 7).map(([label, value]) => ({ label, value, color: tagTone(label).bar }));
  const otherTags = tagRows.slice(7).reduce((sum, [, value]) => sum + value, 0);
  const untagged = inRange.filter((t) => t.tags.length === 0).length;
  if (otherTags + untagged > 0) topTags.push({ label: 'Others', value: otherTags + untagged, color: '#D5D9E0' });

  const recent = [...inRange].sort((a, b) => b.created - a.created || b.ticketNo.localeCompare(a.ticketNo)).slice(0, 8);

  // Status trend: the last 14 days of the range. Until status changes are recorded, each ticket counts from the day
  // it was raised under the status it has now.
  const trendDays = Array.from({ length: 14 }, (_, i) => rangeEnd - (13 - i) * DAY);
  const trend = GROUPS.reduce(
    (acc, g) => ({
      ...acc,
      [g.key]: trendDays.map((day) => tickets.filter((t) => t.group === g.key && t.created <= day).length),
    }),
    {} as Record<GroupKey, number[]>,
  );

  const updateRow = (saved: ApplicationTestingTicketRow) =>
    setRows((current) => current.map((r) => (r.id === saved.id ? saved : r)));

  const setStatus = (row: ApplicationTestingTicketRow, status: number) => {
    if (row.statusId === status || isSaving) return;
    setIsSaving(true);
    applicationTestingService
      .setStatus(row.id, status)
      .then(
        (saved) => {
          updateRow(saved);
          notifySuccess(`${saved.ticketNo} is now ${saved.status}`);
        },
        (error) => notifyError(error),
      )
      .finally(() => setIsSaving(false));
  };

  const setPriority = (row: ApplicationTestingTicketRow, priority: number) => {
    if (row.priorityId === priority || isSaving) return;
    setIsSaving(true);
    applicationTestingService
      .setPriority(row.id, priority)
      .then(
        (saved) => {
          updateRow(saved);
          notifySuccess(`${saved.ticketNo} priority set to ${saved.priority}`);
        },
        (error) => notifyError(error),
      )
      .finally(() => setIsSaving(false));
  };

  const total = inRange.length;
  const kpis = [
    { icon: 'bug_report', color: undefined, value: total, label: 'Total Tickets', previous: previous.length },
    ...statusOptions.map((status) => ({
      icon: KPI_CARDS[status.name]?.icon ?? 'flag',
      color: statusColor(status.id),
      value: counts[status.id],
      label: KPI_CARDS[status.name]?.label ?? status.name,
      previous: previousCounts[status.id],
    })),
  ];

  return (
    <div className="form atd-page">
      <ApplicationTestingSidebar active="dashboard" />

      <main className="atd-main">
        <div className="atd-heading">
          <div>
            <h1>Application Testing Dashboard</h1>
            <p>Overview of bugs, enhancements and issue status</p>
          </div>
          <button type="button" className="atd-range" onClick={rangeMenu.onOpen}>
            <Icon name="calendar_today" outlined />
            <span>{formatDay(rangeStart)}</span>
            <span className="atd-range-dash">-</span>
            <span>{formatDay(rangeEnd)}</span>
            <Icon name="expand_more" />
          </button>
          <Menu {...rangeMenu} className="more-button-menu" xPosition="before">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                role="menuitem"
                className="mat-mdc-menu-item mat-mdc-focus-indicator"
                onClick={() => {
                  setRange(r.key);
                  rangeMenu.onClose();
                }}
              >
                <span className="mat-mdc-menu-item-text">{r.label}</span>
              </button>
            ))}
          </Menu>
        </div>

        <div className="atd-kpis">
          {kpis.map((k) => (
            <KpiCard
              key={k.label}
              icon={k.icon}
              color={k.color}
              value={k.value}
              label={k.label}
              change={percentChange(k.value, k.previous)}
            />
          ))}
        </div>

        <div className="atd-grid-3">
          <section className="atd-card">
            <h2>Tickets by Status</h2>
            <Donut counts={counts} total={total} />
          </section>
          <section className="atd-card">
            <h2>Tickets by Priority</h2>
            <PriorityChart values={priorityValues} />
          </section>
          <section className="atd-card">
            <h2>Tickets by Tag</h2>
            {topTags.length ? <HorizontalBars rows={topTags} /> : <p className="atd-empty">No tagged tickets yet.</p>}
          </section>
        </div>

        <div className="atd-grid-2">
          <section className="atd-card atd-recent application-testing-recent">
            <div className="atd-card-title">
              <h2>Recent Tickets</h2>
              <button type="button" className="atd-link" onClick={() => navigate('/home/application-testing')}>
                View All
                <Icon name="arrow_forward" />
              </button>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Ticket No.</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Tags</th>
                  <th>Created on</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/home/application-testing/${t.id}`)}
                    title={`View ${t.ticketNo}`}
                  >
                    <td>{t.ticketNo}</td>
                    <td className="atd-subject">{t.subject}</td>
                    <td>
                      <StatusPill value={t.statusId} disabled={isSaving} onChange={(s) => setStatus(t, s)} />
                    </td>
                    <td>
                      <PriorityPill value={t.priorityId} disabled={isSaving} onChange={(p) => setPriority(t, p)} />
                    </td>
                    <td>
                      <TagChips tags={t.tags.slice(0, 2)} />
                    </td>
                    <td>{t.date}</td>
                  </tr>
                ))}
                {recent.length === 0 && (
                  <tr>
                    <td colSpan={6} className="atd-empty">
                      No tickets in this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <div className="atd-stack">
            <section className="atd-card">
              <h2>Status Trend (Last 14 Days)</h2>
              <TrendChart days={trendDays} series={trend} />
            </section>
            <section className="atd-card">
              <h2>Top Assignees (by Ticket Count)</h2>
              <HorizontalBars rows={[{ label: 'Unassigned', value: total, color: '#9AA3AF' }]} />
              <p className="atd-note">Tickets are not assigned yet; each one counts as unassigned.</p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
